/**
 * dinero.ts — LA CASA DEL DINERO.
 *
 * Es el único archivo del frente donde el guardián 3 permite tocar importes, y
 * la regla que lo justifica es del contrato de suite §6.1:
 *
 *   > El dinero viaja como texto y nunca como número de JSON.
 *   > Todo importe lleva su moneda en el mismo objeto, nunca implícita.
 *   > Un importe convertido viaja con su tipo de cambio, su fecha y su fuente.
 *
 * `postgres.js` devuelve `numeric` como STRING precisamente para no perder
 * precisión. Meter eso en un `number` de JavaScript tira esa protección a la
 * basura: `0.1 + 0.2` deja de ser `0.3` y un balance de millones se descuadra
 * por céntimos que nadie encuentra.
 *
 * Por eso acá casi no hay aritmética: este módulo FORMATEA, COMPARA y LEE.
 * Los totales de verdad los produce la base; el frente los muestra.
 *
 * ── La única excepción, y por qué ─────────────────────────────────────────
 *
 * `sumar()` existe porque un formulario de asiento tiene que decir si cuadra
 * MIENTRAS la persona teclea, y preguntárselo al servidor en cada tecla es la
 * clase de diseño que este proyecto rechazó por medido: la latencia la manda
 * el número de idas y vueltas.
 *
 * La prohibición era contra `number`, no contra la suma. `sumar()` trabaja en
 * `BigInt` sobre centésimos escalados, que es exactamente lo que hace Postgres
 * con `numeric` y no pierde nada. Eso NO se afirma de palabra: hay un caso en
 * `db/pruebas/exactitud.test.mjs` que suma los mismos valores en la base y acá
 * y exige que den idéntico.
 *
 * Lo que `sumar()` NO es: la autoridad. El servidor decide si un asiento
 * cuadra —la regla vive en la base y un asiento descuadrado queda en
 * borrador—. Esto solo evita que alguien llene veinte líneas para enterarse
 * al final.
 */

/** Un importe siempre viaja con su moneda. Nunca suelto. */
export interface Importe {
  /** El valor tal como vino de la base: texto, con su escala intacta. */
  readonly valor: string;
  /** ISO 4217. Nunca implícita — contrato §6.1. */
  readonly moneda: string;
}

/**
 * Un importe convertido arrastra de dónde salió su conversión.
 *
 * La `fuente` no es adorno: distingue un tipo de cambio que el BCCR declaró
 * vigente para esa fecha de uno que el hub copió del día anterior. Solo el
 * primero sirve para un documento con consecuencia fiscal.
 */
export interface ImporteConvertido extends Importe {
  readonly original: Importe;
  readonly tipoDeCambio: string;
  readonly fechaDelTipo: string;
  readonly fuente: Fuente;
}

/**
 * Qué afirma un tipo de cambio. Nombra el HECHO, no el mecanismo.
 *
 *   vigente_declarado    · el BCCR lo declara vigente PARA esa fecha
 *   copiado_del_anterior · el hub copió el último publicado porque no halló otro
 *
 * Solo el primero respalda un documento con consecuencia fiscal.
 */
export type Fuente = 'vigente_declarado' | 'copiado_del_anterior';

/**
 * Un tipo de cambio NUNCA viaja solo.
 *
 * Antes `convertir` recibía el tipo como una cadena suelta y devolvía un
 * importe pelado: la fecha y la fuente se perdían por el camino. No se notaba
 * porque en la muestra había un solo tipo para todo — pero los asientos tienen
 * fecha, y en cuanto los tipos varíen por día, un importe convertido no sabría
 * cuál lo produjo. La regla «con `copiado_del_anterior` no se contabiliza y no
 * se emite» no tendría contra qué comprobarse.
 *
 * Contrato de suite §6.1 y §11.2, y contrato del hub v0.5 §4.
 */
export interface Cambio {
  /** El tipo, como texto. Nunca un número. */
  readonly valor: string;
  /** La fecha PARA la que rige, no la de publicación. */
  readonly fecha: string;
  readonly fuente: Fuente;
}

/**
 * `true` si el importe convertido puede respaldar un comprobante.
 *
 * Un tipo copiado del día anterior no lo respalda: el asiento se guarda como
 * borrador y no se contabiliza, y una factura con esa línea no se emite. Es
 * una función de una línea a propósito — la regla vive en un solo lugar y se
 * lee igual en todas las pantallas que la apliquen.
 */
export function respaldaComprobante(importe: ImporteConvertido): boolean {
  return importe.fuente === 'vigente_declarado';
}

const SIMBOLOS: Readonly<Record<string, string>> = {
  CRC: '₡',
  USD: '$',
};

/**
 * Formatea para mostrar. No convierte, no suma, no redondea: solo separa
 * miles y decimales según el idioma.
 *
 * El valor se parte en entero y decimales COMO TEXTO — nunca se pasa por
 * `Number`. Un `numeric(18,2)` con dieciocho dígitos no cabe en el entero
 * seguro de JavaScript, y `Intl.NumberFormat` sobre un `number` ya perdido
 * formatea con toda elegancia una cifra equivocada.
 */
export function mostrar(importe: Importe, idioma: 'es' | 'en' = 'es'): string {
  const local = idioma === 'es' ? 'es-CR' : 'en-US';
  const simbolo = SIMBOLOS[importe.moneda] ?? importe.moneda;

  const negativo = importe.valor.trimStart().startsWith('-');
  const limpio = importe.valor.replace('-', '').trim();
  const [entero = '0', decimales = ''] = limpio.split('.');

  const agrupado = new Intl.NumberFormat(local).format(BigInt(entero));
  const separador = idioma === 'es' ? ',' : '.';

  // Los céntimos SIEMPRE se muestran, aunque el valor no los traiga.
  //
  // Un monto tecleado como `6149400` llega sin decimales, y ponerlo al lado de
  // uno que sí los trae —`₡ 6 149 400` junto a `₡ 6 149 400,00`— hace que dos
  // cifras idénticas parezcan distintas. En una columna de totales eso es una
  // llamada al contador.
  //
  // Dos decimales: es la escala del colón y del dólar. Una moneda sin
  // subdivisión necesitaría que la escala viniera con el importe, y ese día
  // esto se parametriza.
  const cola = separador + (decimales || '').padEnd(2, '0').slice(0, 2);

  return `${negativo ? '−' : ''}${simbolo} ${agrupado}${cola}`;
}

/**
 * Un importe sin moneda NO SE MUESTRA.
 *
 * De Skoped Kids, y la razón es suya: si la base se negó a afirmar un saldo
 * —porque la cuenta mezcla dos monedas— ponerle un símbolo en la pantalla es
 * afirmar lo que la base se negó a afirmar.
 */
export function mostrarOptativo(
  importe: Importe | null | undefined,
  idioma: 'es' | 'en' = 'es',
): string {
  if (!importe?.moneda) return '—';
  return mostrar(importe, idioma);
}

/**
 * Parte un importe en signo, entero y decimales, todo como `BigInt`.
 * Seis decimales de escala: suficiente para un tipo de cambio y de sobra para
 * `numeric(18,2)`. Nunca pasa por `Number`.
 */
function descomponer(valor: string) {
  const neg = valor.trimStart().startsWith('-');
  const [e = '0', d = ''] = valor.replace('-', '').trim().split('.');
  return { neg, e: BigInt(e), d: BigInt(d.padEnd(6, '0').slice(0, 6)) };
}

/**
 * Compara dos importes SIN convertirlos.
 *
 * Si las monedas difieren devuelve `null` en vez de un número: comparar sin
 * mirar la moneda produce un resultado correcto en la moneda equivocada, que
 * es justo la mitad silenciosa del guardián 3.
 */
export function comparar(a: Importe, b: Importe): -1 | 0 | 1 | null {
  if (a.moneda !== b.moneda) return null;

  const x = descomponer(a.valor);
  const y = descomponer(b.valor);

  if (x.neg !== y.neg) return x.neg ? -1 : 1;
  const signo = x.neg ? -1 : 1;
  if (x.e !== y.e) return (x.e > y.e ? 1 : -1) * signo as -1 | 1;
  if (x.d !== y.d) return (x.d > y.d ? 1 : -1) * signo as -1 | 1;
  return 0;
}

/** `true` si el importe es exactamente cero. Para el cuadre de un asiento. */
export function esCero(importe: Importe): boolean {
  return /^-?0*(\.0*)?$/.test(importe.valor.trim());
}

/* ═══════════════════════════════════════════════════════════════════════════
   LEER LO QUE LA PERSONA TECLEA
   ═════════════════════════════════════════════════════════════════════════ */

/**
 * Convierte lo que alguien escribió en un campo de monto a TEXTO CANÓNICO:
 * sin agrupadores, con punto decimal, con el signo adelante. `null` si no se
 * puede leer.
 *
 * ⚠️ NO devuelve un número, y la diferencia no es de estilo.
 *
 * El mockup tenía `SB.parseNum`, que devolvía un `number`. Podía permitírselo
 * porque no guardaba nada. Acá el texto que la persona teclea es el ORIGEN de
 * un asiento: si pasa por `Number` en el campo, el dato llega dañado a la base
 * y toda la exactitud que la base defiende ya no defiende nada.
 *
 * ── Cómo decide cuál separador es el decimal ──────────────────────────────
 *
 * El error que esto existe para evitar ya ocurrió una vez en los mockups:
 * `530.00` tecleado en inglés se leyó como 53 000. Dos órdenes de magnitud,
 * sin un solo error en pantalla.
 *
 * La regla no consulta solo el idioma, porque el idioma se equivoca:
 *
 *   1. Si aparecen LOS DOS separadores, el ÚLTIMO es el decimal. No depende
 *      del idioma y no tiene otra lectura posible: `1,234.56` y `1.234,56`
 *      son ambos mil doscientos treinta y cuatro con cincuenta y seis.
 *   2. Si aparece UNO SOLO y está repetido, es agrupador: `1.234.567`.
 *   3. Si aparece UNA SOLA VEZ, es agrupador únicamente cuando lleva
 *      EXACTAMENTE tres dígitos detrás Y no es el decimal del idioma. Un
 *      agrupador siempre lleva tres dígitos; con uno o dos detrás no existe
 *      lectura legal como agrupador, así que es decimal aunque el idioma
 *      diga otra cosa. Ahí es donde se salva `530.00` en español.
 *
 * Lo que NO hace es adivinar: una cadena que no encaja devuelve `null`, y el
 * campo puede pedirla de nuevo. Un monto ilegible se rechaza; nunca se
 * convierte en cero, que es la forma silenciosa de perder plata.
 */
export function leer(escrito: string, idioma: 'es' | 'en' = 'es'): string | null {
  // Espacios de todo tipo, incluido el fino que usa `Intl` en es-CR (U+202F)
  // y el duro (U+00A0) que llega al pegar desde una hoja de cálculo.
  const s = escrito.replace(/[\s\u00A0\u202F]/g, '');
  if (s === '') return null;

  const negativo = s.startsWith('-') || s.startsWith('\u2212');
  const cuerpo = s.replace(/^[-\u2212+]/, '');

  // Solo dígitos, puntos y comas. Cualquier otra cosa —un símbolo de moneda,
  // una letra, un segundo signo— no se limpia: se rechaza.
  if (!/^[\d.,]+$/.test(cuerpo)) return null;

  const puntos = (cuerpo.match(/\./g) ?? []).length;
  const comas = (cuerpo.match(/,/g) ?? []).length;

  // Qué separador se propone como decimal, según la regla de arriba.
  let propuesta: '.' | ',' | null;
  if (puntos > 0 && comas > 0) {
    propuesta = cuerpo.lastIndexOf('.') > cuerpo.lastIndexOf(',') ? '.' : ',';
  } else if (puntos + comas === 0) {
    propuesta = null;
  } else {
    const cual = puntos > 0 ? '.' : ',';
    const detras = cuerpo.length - cuerpo.lastIndexOf(cual) - 1;
    const esAgrupador = puntos + comas > 1 || (detras === 3 && cual !== (idioma === 'es' ? ',' : '.'));
    propuesta = esAgrupador ? null : cual;
  }

  // Las interpretaciones que vale la pena intentar, en orden. La segunda solo
  // existe para cuando el agrupamiento sale MAL FORMADO —`1234.567` en
  // español— porque entonces la única lectura legal que queda es la decimal.
  // No es adivinar: es que no sobrevive otra.
  const intentos: Array<'.' | ',' | null> = [propuesta];
  if (propuesta === null && puntos + comas === 1) intentos.push(puntos > 0 ? '.' : ',');

  for (const decimal of intentos) {
    const canonico = interpretar(cuerpo, decimal);
    if (canonico !== null) return firmar(canonico, negativo);
  }
  return null;
}

/**
 * Interpreta el cuerpo dando por hecho cuál separador es el decimal, y RECHAZA
 * si el agrupamiento no está bien formado.
 *
 * Sin esta validación, `1.2.3,4,5` salía como `1234.5`: basura aceptada en
 * silencio. Un agrupador va siempre entre grupos de tres, y el decimal aparece
 * una sola vez. Lo que no cumpla eso no es un monto mal escrito, es otra cosa.
 */
function interpretar(cuerpo: string, decimal: '.' | ',' | null): string | null {
  let entero: string;
  let decimales = '';

  if (decimal === null) {
    entero = cuerpo;
  } else {
    const corte = cuerpo.lastIndexOf(decimal);
    entero = cuerpo.slice(0, corte);
    decimales = cuerpo.slice(corte + 1);
    // El decimal aparece UNA vez, y detras de el solo van digitos.
    if (entero.includes(decimal) || !SOLO_DIGITOS.test(decimales)) return null;
  }

  const plano = desagrupar(entero);
  if (plano === null) return null;

  if (plano === '' && decimales === '') return null;
  const limpio = plano === '' ? '0' : plano.replace(/^0+(?=\d)/, '');
  return decimales === '' ? limpio : limpio + '.' + decimales;
}

const SOLO_DIGITOS = /^\d*$/;

/**
 * Quita los separadores de miles de la parte entera, comprobando que estuvieran
 * donde tienen que estar: el primer grupo de uno a tres digitos y los demas de
 * tres exactos. Devuelve `null` si no.
 */
function desagrupar(entero: string): string | null {
  if (SOLO_DIGITOS.test(entero)) return entero;

  const separador = entero.includes('.') && entero.includes(',')
    ? null                                   // dos agrupadores distintos: no existe
    : entero.includes('.') ? '.' : ',';
  if (separador === null) return null;

  const grupos = entero.split(separador);
  if (grupos.length < 2) return null;
  if (!/^\d{1,3}$/.test(grupos[0]!)) return null;
  for (const g of grupos.slice(1)) {
    if (!/^\d{3}$/.test(g)) return null;
  }
  return grupos.join('');
}
/**
 * Le pone el signo. Menos cero es cero: que el signo sobreviva a un cero
 * haría que un asiento cuadrado no lo pareciera.
 */
function firmar(texto: string, negativo: boolean): string {
  if (/^0*(\.0*)?$/.test(texto)) return texto;
  return negativo ? `-${texto}` : texto;
}

/* ═══════════════════════════════════════════════════════════════════════════
   LA ÚNICA PUERTA HACIA UN number
   ═════════════════════════════════════════════════════════════════════════ */

/**
 * Convierte un importe a `number` PARA DIBUJARLO. Nada más.
 *
 * Una gráfica necesita números: la altura de una barra es una cantidad de
 * píxeles, no una cifra contable. Pero la conversión que eso exige es
 * exactamente la que el resto del sistema prohíbe, y si cada componente la
 * hiciera por su cuenta, la prohibición no significaría nada.
 *
 * Así que la conversión existe UNA vez, acá, con nombre propio y buscable.
 *
 * ⚠️ Lo que sale de esta función NO SE MUESTRA NUNCA. Ni en una etiqueta, ni
 * en un tooltip, ni en un eje. Un `numeric(18,2)` grande pierde dígitos al
 * pasar por aquí, y el número redondeado se dibuja idéntico —la barra mide lo
 * mismo— pero escrito estaría mal. La etiqueta sale siempre de `mostrar()`,
 * que trabaja sobre el texto.
 *
 * Tampoco se suma, ni se compara, ni se guarda. Entra en un eje y muere ahí.
 */
export function paraDibujar(importe: Importe | string): number {
  const valor = typeof importe === 'string' ? importe : importe.valor;
  const n = Number(valor);
  return Number.isFinite(n) ? n : 0;
}

/* ═══════════════════════════════════════════════════════════════════════════
   SUMAR — la excepción, acotada
   ═════════════════════════════════════════════════════════════════════════ */

/** Escala un texto canónico a `BigInt` con `escala` decimales. */
function aEscala(valor: string, escala: number): bigint {
  const neg = valor.startsWith('-');
  const [e = '0', d = ''] = valor.replace('-', '').split('.');
  const n = BigInt(e + d.padEnd(escala, '0').slice(0, escala));
  return neg ? -n : n;
}

/** Cuántos decimales trae un texto canónico. */
function escalaDe(valor: string): number {
  const punto = valor.indexOf('.');
  return punto === -1 ? 0 : valor.length - punto - 1;
}

/** Devuelve un `BigInt` escalado al texto canónico con esa escala. */
function deEscala(n: bigint, escala: number): string {
  const neg = n < 0n;
  const s = (neg ? -n : n).toString().padStart(escala + 1, '0');
  const entero = s.slice(0, s.length - escala);
  const dec = escala === 0 ? '' : `.${s.slice(s.length - escala)}`;
  const texto = `${entero}${dec}`;
  return neg && !/^0*(\.0*)?$/.test(texto) ? `-${texto}` : texto;
}

/**
 * Suma importes de la MISMA moneda, exacto.
 *
 * Devuelve `null` si las monedas difieren o si la lista está vacía: no hay una
 * respuesta correcta que dar, y devolver cero sería inventarla. Quien llama lo
 * muestra como raya, igual que cualquier importe sin moneda.
 *
 * El resultado conserva la mayor escala de las entradas — no redondea. Si tres
 * líneas traen dos decimales, la suma trae dos; si una trae seis, la suma trae
 * seis. Redondear acá volvería a meter la decisión de dónde se redondea en el
 * frente, que es justo lo que el caso de `exactitud.test.mjs` demostró que
 * cambia el resultado.
 */
export function sumar(importes: readonly Importe[]): Importe | null {
  if (importes.length === 0) return null;

  const moneda = importes[0]!.moneda;
  if (!moneda) return null;
  if (importes.some((i) => i.moneda !== moneda)) return null;

  const escala = Math.max(...importes.map((i) => escalaDe(i.valor)));
  let total = 0n;
  for (const i of importes) total += aEscala(i.valor, escala);

  return { valor: deEscala(total, escala), moneda };
}

/**
 * `a − b`, con la misma disciplina que `sumar`. Es lo que decide si un asiento
 * cuadra: debe menos haber tiene que dar cero.
 */
export function diferencia(a: Importe, b: Importe): Importe | null {
  if (a.moneda !== b.moneda || !a.moneda) return null;
  const escala = Math.max(escalaDe(a.valor), escalaDe(b.valor));
  return {
    valor: deEscala(aEscala(a.valor, escala) - aEscala(b.valor, escala), escala),
    moneda: a.moneda,
  };
}

/**
 * Convierte un importe a la moneda funcional al tipo de cambio dado, POR LÍNEA.
 *
 * `exactitud.test.mjs` demostró que convertir y redondear por línea NO da lo
 * mismo que convertir el total: tres líneas de 10,01 · 10,02 · 10,03 a 512,45
 * dan 15 404,24 por línea y 15 404,25 por el total. La diferencia es de un
 * céntimo, y es exactamente el descuadre que aparece meses después sin que
 * nadie sepa de dónde salió.
 *
 * Nuestra regla es POR LÍNEA, y ésta es la función que la aplica.
 *
 * Redondea a mitad hacia arriba alejándose del cero, que es lo que hace
 * `round(numeric, 2)` en Postgres. No es un detalle: si el frente redondeara
 * al par más cercano —lo que hace `toFixed` en algunos casos— mostraría un
 * céntimo distinto del que guarda la base, y en la pantalla parecería un error
 * de la base. Hay un caso en `db/pruebas/exactitud.test.mjs` que lo comprueba.
 *
 * Devuelve un `ImporteConvertido`, que arrastra el tipo, su fecha y su fuente.
 * No es metadato de adorno: de la `fuente` depende si el asiento se contabiliza
 * o queda en borrador, y si una factura con esa línea se puede emitir.
 *
 * Igual que `sumar`, esto es un ANTICIPO, no la autoridad: el asiento se
 * guarda con la conversión que hace la base.
 */
export function convertir(
  importe: Importe,
  cambio: Cambio,
  monedaDestino: string,
  decimales = 2,
): ImporteConvertido {
  const e1 = escalaDe(importe.valor);
  const e2 = escalaDe(cambio.valor);
  const producto = aEscala(importe.valor, e1) * aEscala(cambio.valor, e2);

  const sobran = e1 + e2 - decimales;
  let escalado: bigint;
  if (sobran <= 0) {
    escalado = producto * 10n ** BigInt(-sobran);
  } else {
    const divisor = 10n ** BigInt(sobran);
    const q = producto / divisor;        // BigInt trunca hacia cero
    const r = producto % divisor;
    escalado = q + ((r < 0n ? -r : r) * 2n >= divisor ? (producto < 0n ? -1n : 1n) : 0n);
  }

  // El resultado ARRASTRA de dónde salió. Ésa es la diferencia con la versión
  // anterior, y es la que permite que la regla del comprobante exista.
  return {
    valor: deEscala(escalado, decimales),
    moneda: monedaDestino,
    original: importe,
    tipoDeCambio: cambio.valor,
    fechaDelTipo: cambio.fecha,
    fuente: cambio.fuente,
  };
}

/**
 * Expresa un importe en otra moneda SOLO PARA LEERLO. No es contabilidad.
 *
 * ⚠️ Esto NO es ninguna de las tres operaciones de la NIC 21.
 *
 *   1. Revaluar partidas monetarias  → genera asientos, va a resultados
 *   2. Convertir a moneda de presentación → va a otro resultado integral
 *   3. **Esto** → no genera nada, no cambia ningún saldo, no es moneda de
 *      presentación. Es una lupa.
 *
 * Tiene función propia y nombre propio precisamente para que no se pueda
 * confundir con `convertir()`, que sí registra. Y la pantalla que la use está
 * obligada a decir que lo es: un balance en dólares sin ese rótulo se lee como
 * un estado en moneda de presentación, que es otra cosa y se prepara distinto.
 *
 * Divide, mientras que `convertir` multiplica. Mismo BigInt, mismo redondeo a
 * mitad alejándose del cero.
 */
export function paraLeerEnOtraMoneda(
  importe: Importe,
  tipoDeCambio: string,
  monedaDeLectura: string,
  decimales = 2,
): Importe {
  const ev = escalaDe(importe.valor);
  const et = escalaDe(tipoDeCambio);
  const divisor = aEscala(tipoDeCambio, et) * 10n ** BigInt(ev);
  if (divisor === 0n) return { valor: '0.00', moneda: monedaDeLectura };

  const numerador = aEscala(importe.valor, ev) * 10n ** BigInt(et + decimales);
  const q = numerador / divisor;
  const r = numerador % divisor;
  const neg = (numerador < 0n) !== (divisor < 0n);
  const alza = (r < 0n ? -r : r) * 2n >= (divisor < 0n ? -divisor : divisor)
    ? (neg ? -1n : 1n) : 0n;

  return { valor: deEscala(q + alza, decimales), moneda: monedaDeLectura };
}
